/**
 * Card System - Swiss Design
 *
 * Pre-composed card styles for common use cases
 *
 * STANDARD: For comfortable office use
 * STRESS: For high-pressure situations with enhanced spacing
 * MOBILE: Compact cards for mobile interfaces
 */

// ============================================================================
// STANDARD CARDS
// ============================================================================

export const SWISS_CARD = {
  /** Base card style without hover */
  base: 'border border-gray-200/60 shadow-[0_2px_12px_rgba(0,0,0,0.04)] bg-white',

  /** Hover effect only */
  hover: 'hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:border-gray-300/60 hover:-translate-y-0.5',

  /** Interactive card with hover effects */
  interactive: 'border border-gray-200/60 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:border-gray-300/60 hover:-translate-y-0.5 transition-all duration-300 bg-white',

  /** Minimal card with subtle styling */
  minimal: 'border border-gray-200/60 bg-white',

  /** Emphasized card with strong border */
  emphasized: 'border-2 border-gray-900 shadow-[0_2px_12px_rgba(0,0,0,0.04)] bg-white',

  /** Glass morphism effect */
  glass: 'bg-white/80 backdrop-blur-xl border border-gray-200/60 shadow-[0_2px_12px_rgba(0,0,0,0.04)]',
} as const;

// ============================================================================
// STRESS-OPTIMIZED CARDS
// ============================================================================

/**
 * Card variants optimized for high-stress environments
 * Larger sizes, increased spacing, enhanced contrast
 */
export const SWISS_CARD_STRESS = {
  /** Base stress card - 50% more padding, enhanced shadow */
  base: 'border-2 border-gray-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.04)] bg-white p-8',

  /** Interactive stress card - larger touch target */
  interactive: 'border-2 border-gray-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:border-gray-300/80 hover:-translate-y-1 transition-all duration-200 bg-white p-8 cursor-pointer',

  /** Emphasized stress card - strong border for critical content */
  emphasized: 'border-2 border-gray-900 shadow-[0_4px_16px_rgba(0,0,0,0.08)] bg-white p-8',

  /** Glass morphism stress variant */
  glass: 'bg-white/98 backdrop-blur-xl border-2 border-gray-200/40 shadow-[0_2px_12px_rgba(0,0,0,0.04)] p-8',
} as const;

// ============================================================================
// MOBILE CARDS
// ============================================================================

/**
 * Compact card styles for mobile
 */
export const SWISS_CARD_MOBILE = {
  /** Base card - compact padding */
  base: 'border-2 border-gray-300 shadow-sm bg-white p-3 rounded-lg',

  /** Interactive - with touch feedback */
  interactive: 'border-2 border-gray-300 shadow-sm bg-white p-3 rounded-lg active:bg-gray-50 active:border-gray-400 transition-colors duration-150',

  /** Emphasized - for critical elements */
  emphasized: 'border-2 border-gray-900 shadow-md bg-white p-3 rounded-lg',

  /** Minimal - for dense lists */
  minimal: 'border border-gray-200 bg-white p-2 rounded',
} as const;

export type SwissCard = keyof typeof SWISS_CARD;
export type SwissCardStress = keyof typeof SWISS_CARD_STRESS;
export type SwissCardMobile = keyof typeof SWISS_CARD_MOBILE;
